import { storage } from '../firebase/config';
import { ref, listAll, getDownloadURL } from 'firebase/storage';

class BackgroundVideoCache {
  constructor() {
    this.cache = new Map(); // Cache for loaded videos
    this.allVideoRefs = null; // Store all video references
    this.isInitialized = false;
    this.pageSize = 12;
    this.loadedPages = new Set(); // Track which pages are loaded
  }

  // Initialize by getting all video references without loading thumbnails
  async initialize() {
    if (this.isInitialized) return;

    try {
      const thumbnailsRef = ref(storage, 'background-thumbnails');
      const result = await listAll(thumbnailsRef);
      
      // Store all references sorted by name for consistent pagination
      this.allVideoRefs = result.items.sort((a, b) => a.name.localeCompare(b.name));
      this.isInitialized = true;
      
      console.log(`BackgroundVideoCache initialized with ${this.allVideoRefs.length} videos`);
    } catch (error) {
      console.error('Error initializing BackgroundVideoCache:', error);
      throw error;
    }
  }

  // Get total number of videos
  getTotalVideoCount() {
    return this.allVideoRefs ? this.allVideoRefs.length : 0;
  }

  // Get total number of pages
  getTotalPages() {
    return Math.ceil(this.getTotalVideoCount() / this.pageSize);
  }

  // Check if a page is loaded
  isPageLoaded(page) {
    return this.loadedPages.has(page);
  }

  // Get videos for a specific page (0-indexed)
  getVideosForPage(page) {
    const startIndex = page * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.getTotalVideoCount());
    
    const videos = [];
    for (let i = startIndex; i < endIndex; i++) {
      const videoRef = this.allVideoRefs[i];
      const video = this.cache.get(videoRef.name);
      if (video) {
        videos.push(video);
      }
    }
    
    return videos;
  }

  // Load thumbnails for a specific page
  async loadPage(page) {
    if (!this.isInitialized) {
      await this.initialize();
    }

    if (this.isPageLoaded(page)) {
      return this.getVideosForPage(page);
    }

    const startIndex = page * this.pageSize;
    const endIndex = Math.min(startIndex + this.pageSize, this.getTotalVideoCount());
    
    console.log(`Loading page ${page} (videos ${startIndex}-${endIndex-1})`);

    const videoPromises = [];
    for (let i = startIndex; i < endIndex; i++) {
      const itemRef = this.allVideoRefs[i];
      
      // Skip if already cached
      if (this.cache.has(itemRef.name)) {
        continue;
      }

      const promise = this.loadSingleVideo(itemRef);
      videoPromises.push(promise);
    }

    try {
      const loadedVideos = await Promise.all(videoPromises);
      
      // Add loaded videos to cache
      loadedVideos.forEach(video => {
        if (video) {
          this.cache.set(video.fileName, video);
        }
      });

      // Mark page as loaded
      this.loadedPages.add(page);
      
      console.log(`Page ${page} loaded successfully`);
      return this.getVideosForPage(page);
    } catch (error) {
      console.error(`Error loading page ${page}:`, error);
      throw error;
    }
  }

  // Load a single video thumbnail
  async loadSingleVideo(itemRef) {
    try {
      const downloadURL = await getDownloadURL(itemRef);
      const fileName = itemRef.name;
      const videoName = fileName.replace(/\.jpg$/i, '');
      
      return {
        id: videoName,
        name: videoName,
        thumbnailUrl: downloadURL,
        fileName: fileName
      };
    } catch (error) {
      console.warn(`Failed to load thumbnail for ${itemRef.name}:`, error);
      return null;
    }
  }

  // Get all loaded videos (for display)
  getAllLoadedVideos() {
    return Array.from(this.cache.values()).sort((a, b) => a.name.localeCompare(b.name));
  }

  // Preload first page
  async preloadFirstPage() {
    try {
      await this.loadPage(0);
      console.log('First page preloaded successfully');
    } catch (error) {
      console.error('Error preloading first page:', error);
    }
  }

  // Clear cache (if needed)
  clearCache() {
    this.cache.clear();
    this.loadedPages.clear();
    console.log('BackgroundVideoCache cleared');
  }
}

// Export a singleton instance
export default new BackgroundVideoCache(); 